import ResizableIframe from "@atoms/embeds/ResizableIframe";
import FacebookEmbedCustom from "@components/Embeds/FacebookEmbedCustom";
import FlourishEmbedCustom from "@components/Embeds/FlourishEmbedCustom";
import InstagramEmbedCustom from "@components/Embeds/InstagramEmbedCustom";
import LiveBlogEmbed from "@components/Embeds/LiveBlogEmbed";
import TiktokEmbedCustom from "@components/Embeds/TiktokEmbedCustom";
import { EmbedType } from "@routes/Article/components/ArticleParagraphElements/utils/enums";
import { XEmbedCustom } from "@sphtech/web2-vernacular-ui/elements/XEmbedCustom";
import { cn } from "@util/helpers";
import { DOMNode, domToReact, Element } from "html-react-parser";

type TransformOptions = string | boolean | void | object | Element | null;

export const createEmbed = (
  node: DOMNode,
  type: EmbedType,
  className?: string
): TransformOptions => {
  if (node.type !== "tag") return null;

  const element = node;

  if (type === EmbedType.Flourish) {
    const el = node;
    const href = el.attribs["data-src"];

    return <FlourishEmbedCustom url={href} className={className} />;
  }

  if (element.name === "div" && type === EmbedType.Embed) {
    const children = element.children as DOMNode[];
    const transformWithType = (child: DOMNode) => createEmbed(child, type);

    return (
      <div className="mb-6">
        {domToReact(children, { replace: transformWithType })}
      </div>
    );
  }

  if (type === EmbedType.X) {
    const el = node;
    const href = el.attribs["data-href"];
    const cssClass = el.attribs["data-cssClass"];

    if (!href) return null;

    return (
      <XEmbedCustom
        url={href}
        className={cn(className, cssClass, "[&>.twitter-tweet]:!my-0")}
      />
    );
  }

  if (type === EmbedType.Instagram) {
    const el = node;
    const href = el.attribs["data-href"];
    const cssClass = el.attribs["data-cssClass"];

    if (!href) return null;

    return <InstagramEmbedCustom url={href} className={cssClass} />;
  }

  if (type === EmbedType.Facebook) {
    const el = node;
    const href = el.attribs["data-href"];
    const cssClass = el.attribs["data-cssClass"];

    return <FacebookEmbedCustom url={href} className={cssClass} />;
  }

  if (element.name === "blockquote" && type === EmbedType.Tiktok) {
    const el = node;
    const href = el.attribs.cite;
    const cssClass = el.attribs["data-cssClass"];

    return <TiktokEmbedCustom url={href} className={cssClass} />;
  }

  if (element.name === "iframe" && type === EmbedType.Embed) {
    const el = node;
    const { src, id, classname, className, height = "500px" } = el.attribs;

    const extraClass = classname || className || ""; // Handle both 'classname' and 'className' attributes

    const { hostname, searchParams } = new URL(src);
    const liveblogId = searchParams.get("id");

    if (hostname.includes("liveblog.com") && liveblogId) {
      return (
        <LiveBlogEmbed
          liveblogId={liveblogId}
          height={height}
          className={extraClass}
        />
      );
    }

    const attributes = {
      className: cn("w-full", extraClass),
      style: { height },
      src,
      id,
    };

    return <ResizableIframe attributes={attributes} />;
  }

  return null;
};
